import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Table, Tag, Button, Input, Space, Row, Col, Statistic,
    Modal, message, Select, Typography, Descriptions, Popconfirm
} from 'antd';
import {
    SearchOutlined, CheckOutlined, CloseOutlined,
    ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
    UserOutlined, PhoneOutlined, MailOutlined, EnvironmentOutlined, DeleteOutlined
} from '@ant-design/icons';
import affiliatesService from '../../common/services/affiliates.service';
import dayjs from 'dayjs';
import './affiliates.scss';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ApplicationsPage = () => {
    const [loading, setLoading] = useState(false);
    const [applications, setApplications] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
    const [filters, setFilters] = useState({ search: '', status: '' });
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);
    const [rejectModalVisible, setRejectModalVisible] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [processing, setProcessing] = useState(false);
    const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });

    const fetchApplications = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...filters
            };
            Object.keys(params).forEach(key => !params[key] && delete params[key]);

            const response = await affiliatesService.getApplications(params);
            if (response.success) {
                setApplications(response.data);
                setPagination(prev => ({
                    ...prev,
                    total: response.pagination?.total || 0
                }));
            }
        } catch (error) {
            console.error('Erreur chargement candidatures:', error);
            message.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [pagination.page, pagination.limit, filters]);

    const fetchStats = async () => {
        try {
            // Fetch counts for each status
            const [pendingRes, approvedRes, rejectedRes] = await Promise.all([
                affiliatesService.getApplications({ status: 'pending', limit: 1 }),
                affiliatesService.getApplications({ status: 'approved', limit: 1 }),
                affiliatesService.getApplications({ status: 'rejected', limit: 1 })
            ]);
            setStats({
                pending: pendingRes.pagination?.total || 0,
                approved: approvedRes.pagination?.total || 0,
                rejected: rejectedRes.pagination?.total || 0
            });
        } catch (error) {
            console.error('Erreur stats:', error);
        }
    };

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    useEffect(() => {
        fetchStats();
    }, []);

    const handleSearch = (value) => {
        setFilters(prev => ({ ...prev, search: value }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleStatusFilter = (value) => {
        setFilters(prev => ({ ...prev, status: value || '' }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleTableChange = (paginationConfig) => {
        setPagination(prev => ({
            ...prev,
            page: paginationConfig.current,
            limit: paginationConfig.pageSize
        }));
    };

    const handleApprove = async (id) => {
        setProcessing(true);
        try {
            const response = await affiliatesService.approveApplication(id);
            if (response.success) {
                message.success(`Candidature approuvée ! Code: ${response.data.user.referralCode}`);
                setDetailModalVisible(false);
                fetchApplications();
                fetchStats();
            } else {
                message.error(response.message || 'Erreur lors de l\'approbation');
            }
        } catch (error) {
            message.error(error.message || 'Erreur lors de l\'approbation');
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!selectedApplication) return;

        setProcessing(true);
        try {
            const response = await affiliatesService.rejectApplication(
                selectedApplication._id,
                rejectReason
            );
            if (response.success) {
                message.success('Candidature rejetée');
                setRejectModalVisible(false);
                setDetailModalVisible(false);
                setRejectReason('');
                fetchApplications();
                fetchStats();
            }
        } catch (error) {
            message.error('Erreur lors du rejet');
        } finally {
            setProcessing(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await affiliatesService.deleteApplication(id);
            if (response.success) {
                message.success('Candidature supprimée');
                fetchApplications();
                fetchStats();
            } else {
                message.error(response.message || 'Erreur lors de la suppression');
            }
        } catch (error) {
            message.error('Erreur lors de la suppression');
        }
    };

    const showDetail = (record) => {
        setSelectedApplication(record);
        setDetailModalVisible(true);
    };

    const getStatusTag = (status) => {
        const statusConfig = {
            pending: { color: 'warning', icon: <ClockCircleOutlined />, text: 'En attente' },
            approved: { color: 'success', icon: <CheckCircleOutlined />, text: 'Approuvée' },
            rejected: { color: 'error', icon: <CloseCircleOutlined />, text: 'Rejetée' }
        };
        const config = statusConfig[status] || statusConfig.pending;
        return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
    };

    const columns = [
        {
            title: 'Nom',
            key: 'name',
            render: (_, record) => (
                <Space>
                    <UserOutlined />
                    <span>{record.firstName} {record.lastName}</span>
                </Space>
            )
        },
        {
            title: 'Téléphone',
            dataIndex: 'phone',
            key: 'phone'
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (email) => email || '-'
        },
        {
            title: 'Ville',
            dataIndex: 'city',
            key: 'city',
            render: (city) => city || '-'
        },
        {
            title: 'Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm')
        },
        {
            title: 'Statut',
            dataIndex: 'status',
            key: 'status',
            render: (status) => getStatusTag(status)
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button size="small" onClick={() => showDetail(record)}>
                        Voir
                    </Button>
                    {record.status === 'pending' && (
                        <>
                            <Button
                                type="primary"
                                size="small"
                                icon={<CheckOutlined />}
                                onClick={() => handleApprove(record._id)}
                            >
                                Approuver
                            </Button>
                            <Button
                                danger
                                size="small"
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    setSelectedApplication(record);
                                    setRejectModalVisible(true);
                                }}
                            >
                                Rejeter
                            </Button>
                        </>
                    )}
                    <Popconfirm
                        title="Supprimer cette candidature ?"
                        description="Cette action est irréversible."
                        onConfirm={() => handleDelete(record._id)}
                        okText="Oui"
                        cancelText="Non"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="applications-page">
            <div className="page-header">
                <Title level={3}>Candidatures Affiliés</Title>
            </div>

            {/* Statistiques */}
            <Row gutter={16} className="stats-row">
                <Col xs={8}>
                    <Card>
                        <Statistic
                            title="En attente"
                            value={stats.pending}
                            valueStyle={{ color: '#faad14' }}
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card>
                        <Statistic
                            title="Approuvées"
                            value={stats.approved}
                            valueStyle={{ color: '#3f8600' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card>
                        <Statistic
                            title="Rejetées"
                            value={stats.rejected}
                            valueStyle={{ color: '#cf1322' }}
                            prefix={<CloseCircleOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filtres */}
            <Card className="filters-card">
                <Space wrap>
                    <Input.Search
                        placeholder="Rechercher..."
                        allowClear
                        onSearch={handleSearch}
                        style={{ width: 250 }}
                        prefix={<SearchOutlined />}
                    />
                    <Select
                        placeholder="Statut"
                        allowClear
                        style={{ width: 150 }}
                        onChange={handleStatusFilter}
                        defaultValue=""
                    >
                        <Option value="">Tous</Option>
                        <Option value="pending">En attente</Option>
                        <Option value="approved">Approuvées</Option>
                        <Option value="rejected">Rejetées</Option>
                    </Select>
                </Space>
            </Card>

            {/* Table */}
            <Card>
                <Table
                    columns={columns}
                    dataSource={applications}
                    rowKey="_id"
                    loading={loading}
                    pagination={{
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: pagination.total,
                        showSizeChanger: true,
                        showTotal: (total) => `${total} candidature(s)`
                    }}
                    onChange={handleTableChange}
                />
            </Card>

            {/* Modal détail */}
            <Modal
                title="Détail de la candidature"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                width={600}
                footer={
                    selectedApplication?.status === 'pending' ? (
                        <Space>
                            <Button onClick={() => setDetailModalVisible(false)}>
                                Fermer
                            </Button>
                            <Button
                                danger
                                icon={<CloseOutlined />}
                                onClick={() => setRejectModalVisible(true)}
                            >
                                Rejeter
                            </Button>
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                loading={processing}
                                onClick={() => handleApprove(selectedApplication._id)}
                            >
                                Approuver
                            </Button>
                        </Space>
                    ) : (
                        <Button onClick={() => setDetailModalVisible(false)}>
                            Fermer
                        </Button>
                    )
                }
            >
                {selectedApplication && (
                    <Descriptions bordered column={1}>
                        <Descriptions.Item label={<><UserOutlined /> Nom</>}>
                            {selectedApplication.firstName} {selectedApplication.lastName}
                        </Descriptions.Item>
                        <Descriptions.Item label={<><PhoneOutlined /> Téléphone</>}>
                            {selectedApplication.phone}
                        </Descriptions.Item>
                        <Descriptions.Item label={<><MailOutlined /> Email</>}>
                            {selectedApplication.email || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label={<><EnvironmentOutlined /> Ville</>}>
                            {selectedApplication.city || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Adresse">
                            {selectedApplication.address || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Comment nous a connu">
                            {selectedApplication.howDidYouHear || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Expérience">
                            <Paragraph style={{ margin: 0 }}>
                                {selectedApplication.experience || '-'}
                            </Paragraph>
                        </Descriptions.Item>
                        <Descriptions.Item label="Motivation">
                            <Paragraph style={{ margin: 0 }}>
                                {selectedApplication.motivation || '-'}
                            </Paragraph>
                        </Descriptions.Item>
                        <Descriptions.Item label="Statut">
                            {getStatusTag(selectedApplication.status)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Date de candidature">
                            {dayjs(selectedApplication.createdAt).format('DD/MM/YYYY HH:mm')}
                        </Descriptions.Item>
                        {selectedApplication.status === 'rejected' && selectedApplication.rejectionReason && (
                            <Descriptions.Item label="Raison du rejet">
                                <Text type="danger">{selectedApplication.rejectionReason}</Text>
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                )}
            </Modal>

            {/* Modal rejet */}
            <Modal
                title="Rejeter la candidature"
                open={rejectModalVisible}
                onCancel={() => {
                    setRejectModalVisible(false);
                    setRejectReason('');
                }}
                onOk={handleReject}
                okText="Rejeter"
                okButtonProps={{ danger: true, loading: processing }}
                cancelText="Annuler"
            >
                <p>Êtes-vous sûr de vouloir rejeter cette candidature ?</p>
                <TextArea
                    rows={4}
                    placeholder="Raison du rejet (optionnel)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                />
            </Modal>
        </div>
    );
};

export default ApplicationsPage;
